import {
  APP_NAME,
  APP_VERSION,
  SCHEMA_VERSION,
  CANONICAL_ROOT,
  CANONICAL_ROOT_LABEL,
  FOLDER_SPECS,
  CONFIG_FILENAME,
  LIBRARY_FILENAME
} from "./constants.js";

function defaultManifest() {
  return {
    format: "QuantResearchToolHubLibrary",
    schemaVersion: SCHEMA_VERSION,
    assetModel: "logical-assets-with-locations",
    updatedAt: new Date().toISOString(),
    items: {}
  };
}

export async function discoverRootKit(graph, account) {
  const root = await graph.getByPath(account, CANONICAL_ROOT);
  if (!root?.folder) return null;
  return hydrateRootKit(graph, account, root);
}

export async function createRootKit(graph, account) {
  const driveRoot = await graph.getDriveRoot(account);
  const quantRoot = await graph.ensureFolder(account, driveRoot.id, CANONICAL_ROOT[0]);
  const hubRoot = await graph.ensureFolder(account, quantRoot.id, CANONICAL_ROOT[1]);
  return hydrateRootKit(graph, account, hubRoot);
}

async function hydrateRootKit(graph, account, hubRoot) {
  const folders = { hub: hubRoot.id };
  const folderItems = { hub: hubRoot };

  for (const spec of FOLDER_SPECS) {
    const parentId = folders[spec.parent];
    if (!parentId) throw new Error(`Root Kit parent folder is missing: ${spec.parent}`);
    const item = await graph.ensureFolder(account, parentId, spec.name);
    folders[spec.key] = item.id;
    folderItems[spec.key] = item;
  }

  const config = {
    format: "QuantResearchToolHubConfig",
    schemaVersion: SCHEMA_VERSION,
    appVersion: APP_VERSION,
    appName: APP_NAME,
    canonicalRoot: CANONICAL_ROOT_LABEL,
    rootItemId: hubRoot.id,
    folders: Object.fromEntries(
      FOLDER_SPECS.map(spec => [spec.key, {
        id: folders[spec.key],
        name: spec.name,
        parent: spec.parent,
        category: spec.category
      }])
    ),
    updatedAt: new Date().toISOString()
  };

  const metadataChildren = await graph.listChildren(account, folders.metadata);
  let configItem = metadataChildren.find(x => x.name === CONFIG_FILENAME && x.file) || null;
  let libraryItem = metadataChildren.find(x => x.name === LIBRARY_FILENAME && x.file) || null;

  configItem = await graph.writeJsonFile(account, folders.metadata, CONFIG_FILENAME, config, {
    itemId: configItem?.id || null
  });

  let manifest = defaultManifest();
  if (libraryItem) {
    let valid = false;
    try {
      const text = await graph.downloadText(account, libraryItem.id);
      const parsed = JSON.parse(text);
      if (parsed?.format === "QuantResearchToolHubLibrary" && parsed.items) {
        manifest = parsed;
        valid = true;
      }
    } catch {
      valid = false;
    }
    if (!valid) {
      // Repair a damaged metadata file. OneDrive version history remains the recovery path.
      libraryItem = await graph.writeJsonFile(account, folders.metadata, LIBRARY_FILENAME, manifest, {
        itemId: libraryItem.id
      });
    }
  } else {
    libraryItem = await graph.writeJsonFile(account, folders.metadata, LIBRARY_FILENAME, manifest);
  }

  if (!libraryItem) {
    libraryItem = await graph.findChild(account, folders.metadata, LIBRARY_FILENAME);
  }

  return {
    root: hubRoot,
    folders,
    folderItems,
    config,
    configItem,
    manifest,
    manifestItem: libraryItem
  };
}

export async function reloadManifest(graph, accountContext) {
  const item = await graph.getItem(accountContext.account, accountContext.rootKit.manifestItem.id);
  const text = await graph.downloadText(accountContext.account, item.id);
  const parsed = JSON.parse(text);
  accountContext.rootKit.manifest = parsed;
  accountContext.rootKit.manifestItem = item;
  return parsed;
}

export async function mutateManifest(graph, accountContext, mutator) {
  let attempt = 0;
  while (attempt < 2) {
    attempt += 1;
    const latest = await reloadManifest(graph, accountContext);
    const next = structuredClone(latest);
    await mutator(next);
    next.format = "QuantResearchToolHubLibrary";
    next.schemaVersion = SCHEMA_VERSION;
    next.assetModel = "logical-assets-with-locations";
    next.updatedAt = new Date().toISOString();

    try {
      const updatedItem = await graph.writeJsonFile(
        accountContext.account,
        accountContext.rootKit.folders.metadata,
        LIBRARY_FILENAME,
        next,
        {
          itemId: accountContext.rootKit.manifestItem.id,
          eTag: accountContext.rootKit.manifestItem.eTag
        }
      );
      accountContext.rootKit.manifest = next;
      accountContext.rootKit.manifestItem = updatedItem;
      return next;
    } catch (error) {
      if (error.status !== 412 || attempt >= 2) throw error;
    }
  }
  throw new Error("The OneDrive metadata file changed on another device. Refresh and try again.");
}
